import DOMPurify from "dompurify";
import { useMemo } from "react";

/**
 * `target="_blank"` 링크에 `rel` 을 **강제로** 붙인다 (#5438).
 *
 * ## 왜 「콘텐츠가 착하다」에 기대면 안 되나
 *
 * 아래에서 `target` 을 허용하는 근거로 처음엔 *"`content/**` 전수에서 `target="_blank"`
 * 139건 중 `rel` 없는 것 0건"* 을 들었다. 그 모집단이 **이 훅의 입력을 다 덮지 못한다** —
 * 소비처 다섯 중 하나가 **원격 API** 다:
 *
 * ```
 * MDViewer(md) · DefaultMd · WordPressPost · news/[id]   → content/**  (정적)
 * ★GameGuideMDViewer  pages/docs/[slug].tsx:15  useGetDoc(slug)
 *     → DocsApi.ts:12  fetch("/sdk-api/api/v1/public-documents/{id}/")
 *     → Markdown.toHtml(data.body) → MDViewer:15 useSanitizedHtml
 * ```
 *
 * (작성 당시 목록이다. `WordPressPost` 는 렌더하는 페이지가 없어 2026-10-01 에 지웠다.)
 *
 * 즉 소독기가 존재하는 이유인 **가장 신뢰할 수 없는 입력**이 소스 스캔 밖에 있다. 그래서
 * 「없다」를 주장하지 않고 **여기서 강제**한다 — 출처와 무관하게 닫힌다.
 *
 * ⚠️ 최신 브라우저는 `target="_blank"` 에 암묵적 `noopener` 를 적용한다(Chrome 88+ ·
 * Firefox 79+ · Safari 12.1+). 이 훅은 그 기본값에 기대지 않기 위한 것이다.
 *
 * ## ★반드시 **격리 인스턴스**여야 한다
 *
 * 초판은 `DOMPurify.addHook(...)` 을 썼는데 그건 **모듈 전역**이다. 형제 `InnerHtml` 이 같은
 * `dompurify` 모듈을 쓰므로(`InnerHtml.tsx:1`), 이 훅이 한 번이라도 렌더되면 **그쪽 소독
 * 결과까지 바뀐다.** 실측:
 *
 * ```
 * 등록 전 : <a target="_blank" href="https://x.test">y</a>
 * 등록 후 : <a target="_blank" href="https://x.test" rel="noopener noreferrer">y</a>
 * ★전역 오염: true
 * ```
 *
 * 그게 `InnerHtml` 의 **문서화된 불변식을 깬다** — 그 파일은 *"소독이 no-op 이어야 한다 ·
 * 제거·추가·속성변경 0"* 을 근거로 서버 원문 / 클라 소독본 패턴을 쓴다(#5422). 번역 문자열의
 * `target="_blank"` **35건 중 33건에 `rel` 이 없어서**, 전역 훅이 붙으면 그 33건에서
 * hydration 속성 불일치가 되살아난다.
 *
 * ⚠️ 그리고 **`inner-html-sanitize-noop` 가드가 그걸 못 본다** — 그 테스트 파일이 이 모듈을
 * import 하지 않아 훅이 등록되지 않기 때문이다. 두 소독기의 결합이 **런타임에만** 있어서
 * 파일 단위 테스트가 구조적으로 못 본다.
 *
 * → 그래서 `DOMPurify(window)` 로 **이 훅 전용 인스턴스**를 만든다. 규칙이 밖으로 새지 않는
 *   것이 주장이 아니라 **구조**가 된다. `sanitize-link-attrs` 가 그 격리를 잠근다.
 */
let purifier: ReturnType<typeof DOMPurify> | null = null;
const getPurifier = () => {
  if (purifier) return purifier;
  purifier = DOMPurify(window);
  purifier.addHook("afterSanitizeAttributes", (node) => {
    const el = node as Element;
    if (node.tagName !== "A" || el.getAttribute?.("target") !== "_blank")
      return;
    /**
     * ⚠️ 기존 `rel` 을 **덮어쓰지 않고 합친다.** 오늘 정적 콘텐츠는 `noopener` /
     * `noreferrer noopener` 뿐이라 덮어써도 손실이 0이지만(실측 96 / 43 · 그 외 토큰 0),
     * 이 훅의 존재 이유가 **정적 코퍼스 밖의 입력**이다 — 원격 문서에 `nofollow`·`ugc`·
     * `sponsored` 가 있으면 조용히 사라진다.
     */
    const prev = el.getAttribute("rel") ?? "";
    const merged = new Set([
      ...prev.split(/\s+/).filter(Boolean),
      "noopener",
      "noreferrer",
    ]);
    el.setAttribute("rel", [...merged].join(" "));
  });
  return purifier;
};

export function useSanitizedHtml(html: string) {
  return useMemo(() => {
    if (typeof window === "undefined") return html;
    return getPurifier().sanitize(html, {
      ADD_TAGS: ["iframe"],
      /**
       * ★`target`·`rel` 을 허용한다 — 없으면 소독이 **링크 동작을 바꾼다** (#5438).
       *
       * DOMPurify 기본 허용목록에 `target` 이 없어서, FAQ 콘텐츠의 `target="_blank"` 가
       * 소독본에서 사라졌다. FAQ 는 클라이언트 렌더라 **사용자가 보는 것이 소독본**이므로,
       * 앱스토어·플레이어 다운로드 링크가 **새 탭 대신 같은 탭에서 열렸다.**
       *
       * 실측(2026-08-31 · `content/**` 전수):
       *
       * ```
       * FAQ 콘텐츠에서 target 을 가진 문자열   67
       *   현재 설정 소실   67   ← 전부 벗겨진다
       *   이 설정 소실      0
       * ```
       *
       * ⚠️ **`rel` 을 함께 허용하고, 위 훅이 그것을 강제한다.** `target="_blank"` 만 살리면
       * reverse tabnabbing 자리가 되는데, 「콘텐츠에 `rel` 이 이미 다 있다」에 기대면 안 된다 —
       * 이 훅의 입력 다섯 중 하나가 **원격 API**라 그 주장의 모집단 밖이다(위 docblock).
       *
       * 참고로 정적 콘텐츠는 오늘 이미 안전하다(`content/**` 전수: `target="_blank"` 139건 ·
       * `rel` 없는 것 0건 · faq 96 · support 42 · getting-started 1). 다만 그건 **근거가 아니라
       * 관측**이고, 근거는 `getPurifier 의 강제` 다.
       *
       * 형제 `InnerHtml` 이 같은 클래스를 `rel` 로 이미 닫았다(#5428) — 그쪽은 **보안 속성**이
       * 벗겨지는 문제였고 이쪽은 **동작 속성**이라 방향이 반대지만, 원인은 같다.
       */
      ADD_ATTR: ["allowfullscreen", "sandbox", "allow", "target", "rel"],
    });
  }, [html]);
}
